import { memo, useDeferredValue } from 'react';
import { Query } from 'src/modules/common/components/Query/Query';
import { FilterValuesGroup } from 'src/common/FilterValuesGroup/FilterValuesGroup';
import { DocumentNode, OperationVariables, skipToken } from '@apollo/client';
import { Filter, FilterConfig } from 'src/utils/composeFilter';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler';
import { AttributeUniqValues } from 'src/generated/operations';
import { GetComparator } from 'src/common/types/Comparator.ts';

type Props<Variables> = {
    query: DocumentNode;
    variables: typeof skipToken | {
        variables: Variables;
    };
    filterConfig: FilterConfig;
    focusedFilterName: string | null;
    filter: Filter;
    getComparator: GetComparator;
    checkboxHandler: CheckboxHandler;
    setFocusedFilterName: (name: string | null) => void;
}

export const FiltersQuery = memo(<
    Data extends Record<string, AttributeUniqValues>,
    Variables extends OperationVariables
>({
    query,
    variables,
    filterConfig,
    focusedFilterName,
    filter,
    getComparator,
    checkboxHandler,
    setFocusedFilterName,
}: Props<Variables>) => {
    const deferredVariables = useDeferredValue(variables);

    return (
        <Query<Data, Variables>
            query={query}
            variables={deferredVariables}
        >
            {({ data }) => {
                if (data === undefined) {
                    return null;
                }
                return filterConfig.map((item) => {
                    const [name, attrId] = item;
                    const { values } = data[name];
                    const filterSelected = filter[name] || [];
                    if (values.length === 0 && filterSelected.length === 0) {
                        return null;
                    }

                    const comparator = getComparator(attrId);

                    const isDisabled = focusedFilterName !== null && focusedFilterName !== name;

                    return (
                        <FilterValuesGroup
                            key={name}
                            filter={filter}
                            name={name}
                            comparator={comparator}
                            options={values}
                            onChange={checkboxHandler}
                            disabled={isDisabled}
                            setFocusedFilterName={setFocusedFilterName}
                            prepareLabel={attrId === '6943' ? (v) => `>>>${v}<<<` : undefined}
                        />
                    );
                });
            }}
        </Query>
    );
});
